import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PublicShell, useSeo } from "../glossary/PublicShell";
import { Spinner } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MapPin, Video, Building2, Globe, Mail, Phone, ShieldCheck, ShieldAlert, ArrowLeft,
  CheckCircle2, ExternalLink, Languages, Baby, Info, Lock, ArrowRight, CalendarPlus, Star, MessageSquarePlus,
} from "lucide-react";

const COST_LABEL = {
  free: "Free consultation", paid: "Paid consultation", not_specified: "Contact for details", varies: "Varies",
};

function Section({ title, children, testid }) {
  return (
    <div className="mt-8" data-testid={testid}>
      <h2 className="font-display text-xl font-bold text-kid-navy mb-3">{title}</h2>
      {children}
    </div>
  );
}

const AGE_OPTIONS = ["0-3", "4-6", "7-9", "10-12", "13-17", "18+", "Not sure"];

function Stars({ value = 0, size = 16, onChange, testid }) {
  const interactive = !!onChange;
  return (
    <div className="inline-flex items-center gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n} size={size}
          onClick={interactive ? () => onChange(n) : undefined}
          className={`${interactive ? "cursor-pointer" : ""} ${n <= Math.round(value) ? "text-kid-sun fill-kid-sun" : "text-kid-line fill-kid-line"}`}
          data-testid={interactive ? `star-${n}` : undefined}
        />
      ))}
    </div>
  );
}

function ReviewDialog({ slug, business, open, onOpenChange, existing, onSaved }) {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [title, setTitle] = useState(existing?.title || "");
  const [comment, setComment] = useState(existing?.comment || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setRating(existing?.rating || 0); setTitle(existing?.title || ""); setComment(existing?.comment || ""); } }, [open, existing]);

  const submit = async () => {
    if (rating < 1) { toast.error("Please choose a star rating."); return; }
    setBusy(true);
    try {
      await api.post(`/marketplace/providers/${slug}/reviews`, { rating, title, comment });
      toast.success("Thanks! Your review will appear once it's approved by our team.");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit your review" : `Review ${business}`}</DialogTitle>
          <DialogDescription>Share your honest experience to help other families. Reviews are moderated before they appear.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-sm block mb-1.5">Your rating *</Label><Stars value={rating} size={28} onChange={setRating} testid="review-stars" /></div>
          <div><Label className="text-sm">Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Patient, kind and effective" className="mt-1.5 bg-white" data-testid="review-title" /></div>
          <div><Label className="text-sm">Your review</Label><Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} placeholder="What was your experience like?" className="mt-1.5 bg-white" data-testid="review-comment" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="review-submit">{busy ? "Submitting…" : existing ? "Update review" : "Submit review"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BookingDialog({ slug, business, open, onOpenChange, user }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", child_age_range: "", preferred_contact: "email", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => {
    if (open) setF((s) => ({ ...s, name: user?.first_name || s.name, email: user?.email || s.email }));
  }, [open, user]);

  const submit = async () => {
    if (!f.name.trim() || !f.email.trim()) { toast.error("Please add your name and email."); return; }
    setBusy(true);
    try {
      await api.post(`/marketplace/providers/${slug}/booking-request`, f);
      toast.success(`Request sent to ${business}. They'll be in touch directly.`);
      onOpenChange(false);
      setF({ name: "", email: "", phone: "", child_age_range: "", preferred_contact: "email", message: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle>Request a booking</DialogTitle>
          <DialogDescription>We'll pass your details to {business}. They'll reach out to you directly — KidPath doesn't process bookings or payments.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-sm">Your name *</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 bg-white" data-testid="booking-name" /></div>
            <div><Label className="text-sm">Email *</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 bg-white" data-testid="booking-email" /></div>
            <div><Label className="text-sm">Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 bg-white" data-testid="booking-phone" /></div>
            <div><Label className="text-sm">Child's age</Label>
              <Select value={f.child_age_range} onValueChange={(v) => set("child_age_range", v)}>
                <SelectTrigger className="mt-1.5 bg-white" data-testid="booking-age"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{AGE_OPTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label className="text-sm">Preferred contact method</Label>
            <Select value={f.preferred_contact} onValueChange={(v) => set("preferred_contact", v)}>
              <SelectTrigger className="mt-1.5 bg-white max-w-xs" data-testid="booking-contact"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="email">Email</SelectItem><SelectItem value="phone">Phone</SelectItem><SelectItem value="either">Either</SelectItem></SelectContent>
            </Select></div>
          <div><Label className="text-sm">Message</Label><Textarea value={f.message} onChange={(e) => set("message", e.target.value)} rows={3} placeholder="Tell them a little about what you're looking for…" className="mt-1.5 bg-white" data-testid="booking-message" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="booking-submit">{busy ? "Sending…" : "Send request"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProviderProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [reviews, setReviews] = useState({ items: [], avg: 0, count: 0, breakdown: {} });
  const [reviewMe, setReviewMe] = useState({ can_review: false, reason: null, review: null });
  const [reviewOpen, setReviewOpen] = useState(false);

  const track = (type) => { api.post(`/marketplace/providers/${slug}/track`, { type }).catch(() => {}); };

  const loadReviews = () => {
    api.get(`/marketplace/providers/${slug}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    if (localStorage.getItem("kidpath_token")) {
      api.get(`/marketplace/providers/${slug}/reviews/me`).then((r) => setReviewMe(r.data)).catch(() => {});
    }
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/marketplace/providers/${slug}`)
      .then((r) => { setP(r.data); track("view"); loadReviews(); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]); // eslint-disable-line

  useSeo({
    title: p ? `${p.business_name} — Ontario Autism Support | KidPath` : "Provider | KidPath",
    description: p ? (p.short_bio || p.headline || `${p.business_name} on KidPath Service Finder.`) : "",
  });

  if (loading) return <PublicShell><div className="py-24"><Spinner /></div></PublicShell>;
  if (notFound || !p) return (
    <PublicShell>
      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-24 text-center">
        <h1 className="font-display text-2xl font-extrabold text-kid-navy">Provider not found</h1>
        <p className="text-kid-muted mt-2">This profile may have been removed or is not yet public.</p>
        <Link to="/providers" className="inline-flex items-center gap-1.5 text-kid-teal font-semibold mt-4"><ArrowLeft size={16} /> Back to Service Finder</Link>
      </section>
    </PublicShell>
  );

  const d = p.delivery || {};
  const formats = [d.virtual && "Virtual", d.in_person && "In person", d.hybrid && "Hybrid", d.home_visits && "Home visits", d.school_visits && "School visits"].filter(Boolean);
  const cred = p.credentials || {};
  const booking = p.booking || {};
  const gated = p.contact_gated;

  return (
    <PublicShell>
      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-10 sm:py-14" data-testid="provider-profile">
        <Link to="/providers" className="inline-flex items-center gap-1.5 text-kid-teal font-semibold text-sm mb-5"><ArrowLeft size={16} /> Back to Service Finder</Link>

        {/* Hero */}
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 rounded-2xl bg-kid-skytint grid place-items-center shrink-0 overflow-hidden">
            {p.logo_url ? <img src={p.logo_url} alt="" className="w-full h-full object-cover" /> : <Building2 size={30} className="text-kid-teal" />}
          </div>
          <div className="flex-1">
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-kid-navy leading-tight">{p.business_name}</h1>
            {p.headline && <p className="text-kid-muted mt-1.5 text-base">{p.headline}</p>}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-sm text-kid-muted">
              {p.rating_count > 0 && (
                <span className="inline-flex items-center gap-1.5" data-testid="provider-rating">
                  <Stars value={p.rating_avg} size={15} />
                  <span className="font-semibold text-kid-navy">{p.rating_avg.toFixed(1)}</span>
                  <span>({p.rating_count})</span>
                </span>
              )}
              {(p.primary_city || p.cities?.[0]) && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {p.primary_city || p.cities[0]}</span>}
              {formats.length > 0 && <span className="inline-flex items-center gap-1"><Video size={14} /> {formats.join(" · ")}</span>}
              {p.accepting_new_clients === "yes" && <span className="inline-flex items-center gap-1 text-[#3f9d5c] font-semibold"><CheckCircle2 size={14} /> Accepting new clients</span>}
            </div>
          </div>
        </div>

        {/* Contact / actions */}
        {gated ? (
          <div className="mt-6 rounded-2xl border border-kid-teal/25 bg-kid-skytint/50 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="provider-contact-gated">
            <div className="flex items-start gap-3">
              <Lock size={20} className="text-kid-teal shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-kid-navy">Contact details & website are a Family Path feature</div>
                <p className="text-sm text-kid-muted mt-0.5">Upgrade to unlock direct email, phone, website and booking links for every provider.</p>
              </div>
            </div>
            <Link to="/app/account" className="inline-flex items-center gap-2 bg-kid-teal text-white font-semibold px-5 py-2.5 rounded-full hover:opacity-90 whitespace-nowrap shrink-0" data-testid="provider-unlock">
              Unlock with Family Path <ArrowRight size={15} />
            </Link>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3 mt-6">
            {booking.external_booking_enabled && booking.external_booking_url ? (
              <a href={booking.external_booking_url} target="_blank" rel="noopener noreferrer" onClick={() => track("click_external_booking")} className="inline-flex items-center gap-2 bg-kid-teal text-white font-semibold px-5 py-2.5 rounded-full hover:opacity-90" data-testid="provider-book-external">
                Book / enquire <ExternalLink size={15} />
              </a>
            ) : booking.accepts_kidpath_requests ? (
              <Button onClick={() => setBookingOpen(true)} className="rounded-full h-11 px-5" data-testid="provider-book-request">
                <CalendarPlus size={15} className="mr-1.5" /> Request a booking
              </Button>
            ) : null}
            {p.website_url && (
              <a href={p.website_url} target="_blank" rel="noopener noreferrer" onClick={() => track("click_website")} className="inline-flex items-center gap-2 border border-kid-line text-kid-navy font-semibold px-5 py-2.5 rounded-full hover:bg-kid-mist" data-testid="provider-website"><Globe size={15} /> Website</a>
            )}
            {p.public_email && (
              <a href={`mailto:${p.public_email}`} onClick={() => track("click_email")} className="inline-flex items-center gap-2 border border-kid-line text-kid-navy font-semibold px-5 py-2.5 rounded-full hover:bg-kid-mist" data-testid="provider-email"><Mail size={15} /> Email</a>
            )}
            {p.public_phone && (
              <a href={`tel:${p.public_phone}`} onClick={() => track("click_phone")} className="inline-flex items-center gap-2 border border-kid-line text-kid-navy font-semibold px-5 py-2.5 rounded-full hover:bg-kid-mist" data-testid="provider-phone"><Phone size={15} /> Call</a>
            )}
          </div>
        )}

        <BookingDialog slug={slug} business={p.business_name} open={bookingOpen} onOpenChange={setBookingOpen} user={user} />

        {/* Services / categories */}
        {(p.categories?.length > 0 || p.service_description) && (
          <Section title="Services" testid="section-services">
            <div className="flex flex-wrap gap-2 mb-3">
              {(p.categories || []).map((c) => <span key={c} className="text-sm bg-kid-skytint text-kid-navy px-3 py-1.5 rounded-full capitalize">{c.replace(/-/g, " ")}</span>)}
            </div>
            {p.service_description && <p className="text-kid-navy/90 leading-relaxed whitespace-pre-wrap">{p.service_description}</p>}
          </Section>
        )}

        {/* About */}
        {(p.short_bio || p.detailed_bio) && (
          <Section title="About" testid="section-about">
            <p className="text-kid-navy/90 leading-relaxed whitespace-pre-wrap">{p.detailed_bio || p.short_bio}</p>
            {p.approach && <><h3 className="font-semibold text-kid-navy mt-4">Our approach</h3><p className="text-kid-navy/90 leading-relaxed whitespace-pre-wrap mt-1">{p.approach}</p></>}
            {p.what_to_expect && <><h3 className="font-semibold text-kid-navy mt-4">What to expect</h3><p className="text-kid-navy/90 leading-relaxed whitespace-pre-wrap mt-1">{p.what_to_expect}</p></>}
          </Section>
        )}

        {/* Who they support */}
        {(p.age_ranges?.length > 0 || p.languages?.length > 0) && (
          <Section title="Who they support" testid="section-audience">
            <div className="grid sm:grid-cols-2 gap-4">
              {p.age_ranges?.length > 0 && (
                <div className="kid-card p-4"><div className="flex items-center gap-2 text-kid-navy font-semibold mb-2"><Baby size={16} /> Age ranges</div>
                  <div className="flex flex-wrap gap-1.5">{p.age_ranges.map((a) => <span key={a} className="text-xs bg-kid-mist px-2 py-1 rounded-full">{a}</span>)}</div></div>
              )}
              {p.languages?.length > 0 && (
                <div className="kid-card p-4"><div className="flex items-center gap-2 text-kid-navy font-semibold mb-2"><Languages size={16} /> Languages</div>
                  <div className="flex flex-wrap gap-1.5">{p.languages.map((l) => <span key={l} className="text-xs bg-kid-mist px-2 py-1 rounded-full">{l}</span>)}</div></div>
              )}
            </div>
          </Section>
        )}

        {/* Locations */}
        {(p.cities?.length > 0 || d.ontario_wide_virtual) && (
          <Section title="Where they work" testid="section-locations">
            <div className="flex flex-wrap gap-2">
              {d.ontario_wide_virtual && <span className="text-sm bg-kid-skytint text-kid-navy px-3 py-1.5 rounded-full">Virtual across Ontario</span>}
              {(p.cities || []).map((c) => <span key={c} className="inline-flex items-center gap-1 text-sm bg-kid-mist px-3 py-1.5 rounded-full"><MapPin size={12} /> {c}</span>)}
            </div>
            {p.accessibility_notes && <p className="text-sm text-kid-muted mt-3">{p.accessibility_notes}</p>}
          </Section>
        )}

        {/* Credentials — provider-submitted */}
        <Section title="Credentials" testid="section-credentials">
          <div className="kid-card p-5">
            {cred.regulated === "yes" ? (
              <div className="flex items-start gap-2">
                <ShieldCheck size={18} className="text-kid-teal shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-kid-navy">{cred.college || "Regulated health professional"}</div>
                  {cred.registration_name && <div className="text-sm text-kid-muted">Registered as: {cred.registration_name}</div>}
                  {cred.registration_number && <div className="text-sm text-kid-muted">Registration #: {cred.registration_number}</div>}
                  {cred.public_register_url && <a href={cred.public_register_url} target="_blank" rel="noopener noreferrer" className="text-sm text-kid-teal font-semibold inline-flex items-center gap-1 mt-1">Verify on public register <ExternalLink size={12} /></a>}
                </div>
              </div>
            ) : (
              <p className="text-sm text-kid-muted">Credential details were not specified. Please confirm qualifications directly with the provider.</p>
            )}
            {cred.additional?.length > 0 && (
              <ul className="mt-3 space-y-1">{cred.additional.map((a, i) => <li key={i} className="text-sm text-kid-navy/90 flex items-center gap-2"><CheckCircle2 size={13} className="text-kid-teal" /> {a}</li>)}</ul>
            )}
            <p className="text-xs text-kid-muted mt-3 flex items-start gap-1.5"><Info size={13} className="shrink-0 mt-0.5" /> Credentials are provider-submitted. Please verify registration and qualifications with the provider and the relevant regulatory college.</p>
          </div>
        </Section>

        {/* Booking note */}
        <Section title="Getting started" testid="section-booking">
          <p className="text-kid-navy/90">
            {COST_LABEL[booking.consultation_cost_type] || "Contact for details"}. Reach out using the buttons above to ask about availability, fit, and fees. KidPath does not process bookings or payments.
          </p>
        </Section>

        {/* Other services from same org */}
        {p.other_services?.length > 0 && (
          <Section title="Other services from this provider" testid="section-other-services">
            <div className="grid sm:grid-cols-2 gap-3">
              {p.other_services.map((s) => (
                <Link key={s.slug} to={`/providers/${s.slug}`} className="kid-card p-4 block hover:shadow-md transition-shadow group" data-testid="other-service-card">
                  <div className="text-xs text-kid-teal font-semibold capitalize">{(s.primary_category || "").replace(/-/g, " ")}</div>
                  <div className="font-semibold text-kid-navy mt-1 group-hover:text-kid-teal transition-colors">{s.headline || "View service"}</div>
                </Link>
              ))}
            </div>
          </Section>
        )}

        {/* Reviews */}
        <Section title="Reviews from families" testid="section-reviews">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="text-4xl font-extrabold text-kid-navy font-display">{(reviews.avg || 0).toFixed(1)}</div>
              <div>
                <Stars value={reviews.avg || 0} size={18} />
                <div className="text-sm text-kid-muted mt-0.5">{reviews.count || 0} review{reviews.count === 1 ? "" : "s"}</div>
              </div>
            </div>
            {reviewMe.can_review ? (
              <Button onClick={() => setReviewOpen(true)} variant="outline" className="rounded-full" data-testid="write-review-btn">
                <MessageSquarePlus size={15} className="mr-1.5" /> {reviewMe.review ? "Edit your review" : "Write a review"}
              </Button>
            ) : reviewMe.reason ? (
              <p className="text-xs text-kid-muted max-w-xs sm:text-right" data-testid="review-locked-reason">{reviewMe.reason}</p>
            ) : null}
          </div>

          {reviewMe.review && (
            <div className="rounded-xl bg-kid-skytint/40 border border-kid-teal/20 p-3 mb-4 text-sm" data-testid="my-review-status">
              Your review (<span className="capitalize font-semibold">{reviewMe.review.status}</span>){reviewMe.review.status === "pending" ? " — it'll appear here once approved." : reviewMe.review.status === "rejected" ? " — it wasn't published." : "."}
            </div>
          )}

          {reviews.items.length === 0 ? (
            <p className="text-kid-muted text-sm" data-testid="reviews-empty">No reviews yet. Families who've connected with this provider through KidPath can share their experience.</p>
          ) : (
            <div className="space-y-4" data-testid="reviews-list">
              {reviews.items.map((r) => (
                <div key={r.id} className="kid-card p-5" data-testid="review-item">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Stars value={r.rating} size={15} />
                      {r.title && <span className="font-semibold text-kid-navy">{r.title}</span>}
                    </div>
                    <span className="text-xs text-kid-muted">{(r.created_at || "").slice(0, 10)}</span>
                  </div>
                  {r.comment && <p className="text-kid-navy/90 leading-relaxed mt-2 whitespace-pre-wrap">{r.comment}</p>}
                  <div className="text-xs text-kid-muted mt-2">— {r.reviewer_name}</div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <ReviewDialog slug={slug} business={p.business_name} open={reviewOpen} onOpenChange={setReviewOpen} existing={reviewMe.review} onSaved={loadReviews} />

        {/* Disclaimer */}
        <div className="mt-10 flex items-start gap-2 text-xs text-kid-muted border-t border-kid-line pt-6" data-testid="profile-disclaimer">
          <ShieldAlert size={14} className="shrink-0 mt-0.5" />
          <span>KidPath provides general information and service navigation only — not medical, legal, financial, or funding-eligibility advice. This profile is provider-submitted; please verify all details directly with the provider.</span>
        </div>
      </section>
    </PublicShell>
  );
}
